/**
 * Ce qu'on entend, et ce qu'on sent dans la main.
 *
 * Le § 3.1 du plan compte « pas de son, pas d'haptique — un jeu de dés muet » parmi
 * les défauts de la v1. Le corriger, ce n'est pas coller un fichier audio sur un
 * clic : c'est décider **quoi** sonne, **quand**, et **avec quelle force**. Cette
 * décision-là se teste ; la synthèse audio, non.
 *
 * D'où le partage : ici une table pure, du motif d'annonce vers un son et une
 * vibration ; à côté, un bruiteur qui ne sait rien du jeu et joue ce qu'on lui donne.
 *
 * ## Pourquoi on part des annonces
 *
 * Elles se déduisent déjà de l'ÉTAT et non des événements du moteur (§ 6.4), ce qui
 * est exactement ce qu'il faut : un son bâti sur les événements se perdrait en ligne,
 * là où Colyseus ne garde que la dernière valeur par intervalle de patch. Le bruitage
 * hérite gratuitement de cette propriété — et du même coup, tout ce qu'on annonce
 * s'entend, sans qu'on ait deux listes à tenir d'accord.
 *
 * ## L'haptique est avare, et c'est une décision
 *
 * On ne vibre que sur ce qui change la partie : le 421, le sec, le rampeau, la
 * bascule en décharge, la fin. Pas sur les jetons — il en bouge plusieurs par manche
 * — et **jamais** sur les chocs de dés : un dé touche cinq à dix fois par jet, trois
 * dés à la fois, et le téléphone vibrerait deux secondes d'affilée à chaque lancer.
 */

import type { Annonce, Motif } from "../jeu/annonces.js";
import type { Impact } from "../scene/impacts.js";

/** Les timbres du jeu. Chacun est synthétisé — aucun fichier, aucune licence. */
export type NomDeSon =
  | "choc"        // un dé qui touche le tapis ou le bourrelet
  | "lancer"      // le geste, avant que les dés touchent
  | "garde"       // un dé mis de côté
  | "relache"     // un dé rendu au jeu
  | "jeton"       // des jetons qui changent de main
  | "sec"         // le coup de menton qui bride toute la table
  | "rampeau"     // le duel
  | "cloche"      // 421
  | "grincement"  // la nénette
  | "bascule"     // la décharge
  | "liberation"  // un joueur sorti d'affaire
  | "fin";

export interface Effet {
  /** Stable et dérivé du contenu : c'est ce qui permet de ne pas rejouer deux fois. */
  readonly id: string;
  readonly son: NomDeSon;
  /** Volume relatif, de 0 à 1. */
  readonly force: number;
  /** Motif de vibration en millisecondes, vide si l'on ne vibre pas. */
  readonly vibration: readonly number[];
}

interface Reglage {
  readonly son: NomDeSon;
  readonly force: number;
  readonly vibration: readonly number[];
}

/**
 * Un son par motif d'annonce. **Exhaustive par construction** : le type interdit
 * d'ajouter un motif sans lui donner un timbre, et un motif muet est une annonce
 * qu'on voit sans l'entendre.
 */
const PAR_MOTIF: Readonly<Record<Motif, Reglage>> = {
  "421": { son: "cloche", force: 1, vibration: [30, 45, 30, 45, 110] },
  nenette: { son: "grincement", force: 0.55, vibration: [] },
  sec: { son: "sec", force: 0.9, vibration: [70] },
  rampeau: { son: "rampeau", force: 0.8, vibration: [20, 60, 20] },
  jetons: { son: "jeton", force: 0.5, vibration: [] },
  decharge: { son: "bascule", force: 0.85, vibration: [60, 60, 60] },
  sauve: { son: "liberation", force: 0.6, vibration: [15, 45, 15] },
  fin: { son: "fin", force: 0.9, vibration: [130] },
};

/** Tous les motifs qui produisent un son. Sert de garde-fou aux tests. */
export const MOTIFS_SONORES = Object.keys(PAR_MOTIF) as readonly Motif[];

/** Le bruitage de ce qui vient d'être annoncé, dans l'ordre. */
export function effetsDesAnnonces(annonces: readonly Annonce[]): readonly Effet[] {
  return annonces.map((annonce) => ({ id: annonce.id, ...PAR_MOTIF[annonce.motif] }));
}

/**
 * Le claquement d'un dé qui touche.
 *
 * L'identifiant mêle l'instant et le rang : deux dés peuvent toucher à la même image
 * — c'est même ce qu'on entend quand on jette trois dés d'un coup — et il ne faut pas
 * qu'un des deux passe pour une répétition de l'autre.
 */
export function effetDuChoc(impact: Impact, rang: number): Effet {
  return {
    id: `choc-${impact.seconde.toFixed(3)}-${rang}`,
    son: "choc",
    // Un choc mou reste audible : le plancher évite qu'un dé qui roule doucement
    // devienne inaudible, ce qui donnerait l'impression d'un jet qui saute.
    force: 0.25 + 0.75 * Math.min(1, Math.max(0, impact.force)),
    vibration: [],
  };
}

/** Le geste du lancer : d'autant plus plein qu'on jette de dés. */
export function effetDeLancer(des: number): Effet {
  return {
    id: `lancer-${des}`,
    son: "lancer",
    force: 0.3 + 0.2 * Math.min(3, Math.max(1, des)),
    vibration: [],
  };
}

/** Mettre un dé de côté, ou le rendre au jeu. */
export function effetDeGarde(garde: boolean): Effet {
  return {
    id: garde ? "garde" : "relache",
    son: garde ? "garde" : "relache",
    force: 0.5,
    vibration: [10],
  };
}
